/**
 * voicePick.js (iter144)
 *
 * Web Speech API wrapper + grammar parser for voice picking.
 *
 * Exports:
 *   - useVoicePick({ enabled, onCommand }) — React hook. Owns the
 *     SpeechRecognition lifecycle. Continuously listens when `enabled`,
 *     auto-restarts on end/error, surfaces transcript + last command.
 *
 *   - parseVoiceCommand(transcript) — Pure function. Maps a recognized
 *     phrase to a typed command:
 *       { kind: 'qty',    qty: number }
 *       { kind: 'check',  digits: string }   // 1-3 char check digit
 *       { kind: 'sku',    last4: string }    // last 4 chars normalized
 *       { kind: 'cmd',    cmd: 'repeat'|'skip'|'stop'|'resume'|'short', n?: number }
 *       null  (no match)
 *
 *   - expectedCheckDigits(location_code)
 *   - skuLast4(sku_code)
 *
 * Designed for noisy warehouse environments — defensive to partial
 * matches and number-word slop ("for" → 4, "to" → 2, etc.).
 */
import { useEffect, useRef, useState } from 'react';

// ----------------------------- Grammar tables ---------------------------

const NUMBER_WORDS = {
  zero: 0, oh: 0, o: 0,
  one: 1, won: 1,
  two: 2, to: 2, too: 2,
  three: 3, tree: 3,
  four: 4, for: 4, fore: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8, ate: 8,
  nine: 9, niner: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14, forteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40, fourty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
  hundred: 100,
};

// NATO phonetic — covers operators spelling alphanumeric SKU suffixes
const NATO = {
  alpha: 'A', alfa: 'A',
  bravo: 'B',
  charlie: 'C',
  delta: 'D',
  echo: 'E',
  foxtrot: 'F',
  golf: 'G',
  hotel: 'H',
  india: 'I',
  juliet: 'J', juliett: 'J',
  kilo: 'K',
  lima: 'L',
  mike: 'M',
  november: 'N',
  oscar: 'O',
  papa: 'P',
  quebec: 'Q',
  romeo: 'R',
  sierra: 'S',
  tango: 'T',
  uniform: 'U',
  victor: 'V',
  whiskey: 'W',
  xray: 'X', 'x-ray': 'X',
  yankee: 'Y',
  zulu: 'Z',
};

const COMMAND_WORDS = {
  repeat: 'repeat', again: 'repeat',
  skip: 'skip', next: 'skip',
  stop: 'stop', pause: 'stop',
  resume: 'resume', start: 'resume', go: 'resume',
  short: 'short',
};

// --------------------------- Public utilities ---------------------------

/**
 * Last 2 numeric digits of a location code make up the check phrase the
 * operator must say to confirm they're at the right bin. Falls back to
 * the full digits when the code has fewer than 2.
 */
export const expectedCheckDigits = (location_code) => {
  if (!location_code) return null;
  const digits = String(location_code).replace(/\D/g, '');
  if (!digits) return null;
  return digits.slice(-2).padStart(2, '0');
};

/**
 * Last 4 chars of the SKU code, normalized (uppercase, alphanumeric only).
 * The verbal-confirmation grammar matches against this.
 */
export const skuLast4 = (sku_code) => {
  if (!sku_code) return null;
  const norm = String(sku_code).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (norm.length < 2) return null;
  return norm.slice(-4);
};

// ----------------------------- Parser -----------------------------------

/**
 * Convert a tokenized phrase to its integer value if recognisable as a
 * compound number ("twenty five" → 25, "one hundred and three" → 103).
 * Returns null if the tokens don't form a coherent number.
 */
const tokensToNumber = (tokens) => {
  if (!tokens.length) return null;
  let total = 0;
  let current = 0;
  let matched = false;
  for (const tok of tokens) {
    if (tok === 'and') continue;
    if (/^\d+$/.test(tok)) {
      current += parseInt(tok, 10);
      matched = true;
      continue;
    }
    const n = NUMBER_WORDS[tok];
    if (n === undefined) return null;
    matched = true;
    if (n === 100) {
      current = Math.max(current, 1) * 100;
    } else if (n >= 20) {
      current += n;
    } else {
      current += n;
    }
  }
  total += current;
  return matched ? total : null;
};

/**
 * Map a spelled-out string of NATO letters and digit-words to a compact
 * alphanumeric. "alpha seven two three" → "A723".
 */
const tokensToAlnum = (tokens) => {
  const out = [];
  for (const tok of tokens) {
    if (/^\d$/.test(tok)) { out.push(tok); continue; }
    if (/^\d+$/.test(tok)) { out.push(...tok.split('')); continue; }
    if (NATO[tok]) { out.push(NATO[tok]); continue; }
    const n = NUMBER_WORDS[tok];
    if (n !== undefined && n <= 9) { out.push(String(n)); continue; }
    // Single ASCII letter spoken bare ("A", "B")
    if (tok.length === 1 && /[a-z]/.test(tok)) { out.push(tok.toUpperCase()); continue; }
  }
  return out.join('');
};

export const parseVoiceCommand = (raw) => {
  if (!raw) return null;
  const clean = String(raw).toLowerCase().trim().replace(/[.,!?]/g, '');
  if (!clean) return null;
  const tokens = clean.split(/\s+/);

  // 1) Direct command word as the first token wins
  const cmdWord = COMMAND_WORDS[tokens[0]];
  if (cmdWord) {
    if (cmdWord === 'short') {
      const n = tokensToNumber(tokens.slice(1));
      return { kind: 'cmd', cmd: 'short', n: n ?? null, raw };
    }
    return { kind: 'cmd', cmd: cmdWord, raw };
  }

  // 2) "CHECK <digits>"
  if (tokens[0] === 'check') {
    const n = tokensToNumber(tokens.slice(1));
    if (n !== null) {
      return { kind: 'check', digits: String(n).padStart(2, '0'), raw };
    }
  }

  // 3) "SKU <last-4>"
  if (tokens[0] === 'sku' || tokens[0] === 'item') {
    const last4 = tokensToAlnum(tokens.slice(1));
    if (last4) return { kind: 'sku', last4: last4.slice(-4), raw };
  }

  // 4) Pure number → quantity
  const qty = tokensToNumber(tokens);
  if (qty !== null && qty >= 0 && qty <= 9999) {
    return { kind: 'qty', qty, raw };
  }

  // 5) NATO-only / mixed digit-letter spelling → SKU last-4
  const alnum = tokensToAlnum(tokens);
  if (alnum.length >= 2 && /[A-Z]/.test(alnum)) {
    return { kind: 'sku', last4: alnum.slice(-4), raw };
  }

  return null;
};

// ----------------------------- React hook -------------------------------

const getRecognizer = () => {
  if (typeof window === 'undefined') return null;
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
};

export const isVoiceSupported = () => {
  if (typeof window === 'undefined') return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

/**
 * useVoicePick — long-running speech recognition wrapper.
 *
 *   const { listening, transcript, lastCommand, error, supported } =
 *     useVoicePick({ enabled, onCommand });
 *
 * When `enabled` flips true: starts recognition, auto-restarts on `end`
 * (Chrome stops every ~1 min) and `no-speech` errors. When `enabled`
 * flips false: stops + clears.
 *
 * `onCommand(cmd)` fires for every parsed command (the parsed object
 * from parseVoiceCommand). The parent owns the state machine.
 */
export const useVoicePick = ({ enabled, onCommand, lang = 'en-AU' }) => {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [lastCommand, setLastCommand] = useState(null);
  const [error, setError] = useState(null);
  const recognizerRef = useRef(null);
  const enabledRef = useRef(enabled);
  const onCommandRef = useRef(onCommand);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { onCommandRef.current = onCommand; }, [onCommand]);

  useEffect(() => {
    if (!enabled) {
      const r = recognizerRef.current;
      if (r) {
        try { r.onend = null; r.stop(); } catch { /* ignore */ }
      }
      setListening(false);
      return;
    }

    if (!isVoiceSupported()) {
      setError('not_supported');
      return;
    }

    const rec = getRecognizer();
    if (!rec) {
      setError('not_supported');
      return;
    }
    recognizerRef.current = rec;
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = lang;
    rec.maxAlternatives = 3;

    rec.onstart = () => { setListening(true); setError(null); };
    rec.onerror = (ev) => {
      const code = ev?.error || 'unknown';
      // 'no-speech' / 'aborted' are noise — don't surface as an error
      if (code !== 'no-speech' && code !== 'aborted') setError(code);
    };
    rec.onend = () => {
      // Auto-restart if still enabled — Chrome times out after ~60s.
      setListening(false);
      if (enabledRef.current) {
        try { rec.start(); } catch { /* race with double-start */ }
      }
    };
    rec.onresult = (ev) => {
      // Walk every new final result, pick the highest-confidence alternative.
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (!r.isFinal) continue;
        let best = r[0];
        for (let j = 1; j < r.length; j++) {
          if ((r[j].confidence ?? 0) > (best.confidence ?? 0)) best = r[j];
        }
        const text = (best.transcript || '').trim();
        if (!text) continue;
        setTranscript(text);
        const parsed = parseVoiceCommand(text);
        if (parsed) {
          setLastCommand({ ...parsed, at: Date.now() });
          try { onCommandRef.current?.(parsed); } catch { /* swallow */ }
        }
      }
    };

    try { rec.start(); } catch (e) { /* already started — ignore */ }

    return () => {
      try { rec.onend = null; rec.stop(); } catch { /* ignore */ }
      recognizerRef.current = null;
      setListening(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, lang]);

  return { listening, transcript, lastCommand, error, supported: isVoiceSupported() };
};
